import { useContext } from "react";
import { AccessContext } from "../components/App/App";
import AccessService from "../../services/AccessService";
import { CompanyAccess, PermissionScope } from "../../main/graphql/types";

export interface UseRoleAccessOptions {
    scope?: PermissionScope;
}

export interface UseRoleAccessConfig {
    permission?: CompanyAccess;
    anyPermissions?: CompanyAccess[];
    allPermissions?: CompanyAccess[];
    scope?: PermissionScope;
}

type UseRoleAccess = {
    (permission: CompanyAccess, options?: UseRoleAccessOptions): boolean;
    (config: UseRoleAccessConfig): boolean;
};

const resolveRoleAccess = (accessService: AccessService, config: UseRoleAccessConfig): boolean => {
    const hasPermissionSelector = config.permission != null;
    const hasAnySelector = Array.isArray(config.anyPermissions);
    const hasAllSelector = Array.isArray(config.allPermissions);

    const selectorCount = Number(hasPermissionSelector) + Number(hasAnySelector) + Number(hasAllSelector);

    if (selectorCount !== 1) {
        return false;
    }

    if (hasPermissionSelector) {
        return accessService.hasPermission(config.permission!, { scope: config.scope });
    }

    if (hasAnySelector) {
        // Empty arrays must fail closed to avoid accidental access via vacuous truth.
        return config.anyPermissions!.length > 0
            ? accessService.hasAnyPermission(config.anyPermissions!, { scope: config.scope })
            : false;
    }

    // Empty arrays must fail closed to avoid accidental access via vacuous truth.
    return config.allPermissions!.length > 0
        ? accessService.hasAllPermissions(config.allPermissions!, { scope: config.scope })
        : false;
};

export const useRoleAccess: UseRoleAccess = (
    permissionOrConfig: CompanyAccess | UseRoleAccessConfig,
    options?: UseRoleAccessOptions
): boolean => {
    const { isReady } = useContext(AccessContext);
    const accessService = AccessService.getInstance();

    if (!isReady) {
        return false;
    }

    if (!accessService.isRoleAccessReady()) {
        return false;
    }

    if (accessService.isRoleAccessLoading()) {
        return false;
    }

    const config: UseRoleAccessConfig =
        permissionOrConfig !== null && typeof permissionOrConfig === "object"
            ? permissionOrConfig
            : {
                  permission: permissionOrConfig as CompanyAccess,
                  scope: options?.scope,
              };

    return resolveRoleAccess(accessService, config);
};

export const useRoleAccessAny = (permissions: CompanyAccess[], options?: UseRoleAccessOptions): boolean => {
    return useRoleAccess({
        anyPermissions: permissions,
        scope: options?.scope,
    });
};

export const useRoleAccessAll = (permissions: CompanyAccess[], options?: UseRoleAccessOptions): boolean => {
    return useRoleAccess({
        allPermissions: permissions,
        scope: options?.scope,
    });
};
